import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleCategoryClick = (slug: string) => {
    navigate(`/category/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-vip-diamond-line text-lg"></i>
            </div>
            <span className="font-heading text-lg font-bold text-foreground-950 whitespace-nowrap">
              国产精品一区
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              首页
            </Link>

            <div className="relative group">
              <button className="px-4 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1">
                分类浏览
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-0 mt-1 w-48 bg-background-50 rounded-lg border border-background-200/70 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 py-2">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => handleCategoryClick(cat.slug)}
                    className="w-full text-left px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <i className={`${cat.icon} text-base text-primary-500`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>

            <Link
              to="/about"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/about')
                  ? 'bg-primary-100 text-primary-700'
                  : 'text-foreground-700 hover:text-foreground-950 hover:bg-background-100'
              }`}
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              关于我们
            </Link>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200/70">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/') ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              首页
            </Link>
            <div className="px-4 py-2 text-xs font-medium text-foreground-500 uppercase tracking-wider">
              分类浏览
            </div>
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryClick(cat.slug)}
                className="w-full text-left px-4 py-3 rounded-lg text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-3"
              >
                <i className={`${cat.icon} text-base text-primary-500`}></i>
                {cat.name}
              </button>
            ))}
            <Link
              to="/about"
              className={`block px-4 py-3 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isActive('/about') ? 'bg-primary-100 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
              }`}
            >
              关于我们
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}